// Strategy view (LAB) — the Build side panel's editor for one security: a stack
// of collapsible strategy sub-boxes. Collapsed shows a summary; expanded reveals
// the full editor and makes that strategy ACTIVE (the one the chart draws).
// Self-contained DOM + its own storage listeners, so it never fights the
// watchlist's rebuild loop and slider drags stay smooth.
import { el, dockIntoSidePanel, closeButton, svgIcon, pressable } from "./dom.js";
import { onStorageChange, storageSet } from "./storage.js";
import { loadBook, saveBook, loadStats, statsStorageKey, bookStorageKey, blankStoredStrategy, type StudioBook, type StudioStats, type StoredStrategy } from "./studio-store.js";
import { renderStrategyEditor, type EditorTab } from "./strategy-editor.js";
import { compileStudio, WHY_DEFS, WHY_ICON } from "./studio-model.js";
import { saveStrategy, saveNamedStrategy } from "./composable/storage.js";

export function renderStrategyView(sym: string, opts: { onBack: () => void }): { el: HTMLElement; dispose: () => void } {
  // Hover swap (pure CSS so it survives rebuilds): a card shows its name at rest
  // and the icon palette on mouse-over — never both at once.
  if (!document.getElementById("ta-strat-card-style")) {
    const st = document.createElement("style");
    st.id = "ta-strat-card-style";
    st.textContent = ".ta-strat-icons{display:none;} .ta-strat-card:hover .ta-strat-name{display:none;} .ta-strat-card:hover .ta-strat-icons{display:flex;}";
    document.head.appendChild(st);
  }
  const root = el("div", { style: { display: "flex", flexDirection: "column", height: "100%", minHeight: "0" } });
  let book: StudioBook = { strategies: [], activeId: null };
  const summaryNodes = new Map<string, HTMLElement>();
  let saveTimer: number | null = null;
  let dragId: string | null = null; // strategy being drag-reordered
  const selTab = new Map<string, EditorTab>(); // which tab each expanded strategy shows

  const reorder = (draggedId: string, targetId: string, after: boolean) => {
    const arr = book.strategies;
    const from = arr.findIndex((s) => s.id === draggedId);
    if (from < 0) return;
    const [moved] = arr.splice(from, 1);
    let to = arr.findIndex((s) => s.id === targetId);
    if (to < 0) { arr.splice(from, 0, moved!); return; }
    if (after) to += 1;
    arr.splice(to, 0, moved!);
    void saveBook(sym, book);
    build();
  };

  const persist = () => {
    if (saveTimer != null) return; // trailing throttle (~80ms) keeps live drags smooth
    saveTimer = window.setTimeout(() => { saveTimer = null; void saveBook(sym, book); }, 80);
  };

  function summaryText(id: string, s: StudioStats | null): string {
    if (!s || s.id !== id || !s.ready) return "not tuned yet";
    return `${s.trades} trades · ${s.winPct.toFixed(0)}% · ${s.expectancyR >= 0 ? "+" : ""}${s.expectancyR.toFixed(2)}R`;
  }

  const list = el("div", { style: { flex: "1", overflowY: "auto", padding: "8px 10px", display: "flex", flexDirection: "column", gap: "8px" } });

  const addStrategy = () => { const s = blankStoredStrategy(`${sym} strategy ${book.strategies.length + 1}`); book.strategies.push(s); book.activeId = s.id; void saveBook(sym, book); build(); };

  function build() {
    // Header: back to watchlist + symbol.
    const header = el("div", { style: { display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderBottom: "1px solid var(--line,#e4e7ec)", flex: "none" } }, [
      (() => { const b = el("button", { text: "‹", attrs: { title: "Back to watchlist" }, style: { border: "0", background: "none", cursor: "pointer", fontSize: "18px", color: "var(--text-secondary)", padding: "0 4px" } }); b.onclick = opts.onBack; return b; })(),
      el("div", { text: sym, style: { fontWeight: "700", flex: "1" } }),
    ]);

    list.replaceChildren();
    summaryNodes.clear();
    for (const strat of book.strategies) {
      const expanded = book.activeId === strat.id;
      const box = el("div", { attrs: { class: "ta-strat-card" }, style: { border: "1px solid var(--line,#e4e7ec)", borderRadius: "10px", overflow: "hidden", background: "var(--bg-surface,#fff)", transition: "box-shadow .12s ease" } });
      // Hover lift — the same soft drop shadow the security cards use.
      box.addEventListener("pointerenter", () => { if (!dragId) box.style.boxShadow = "0 2px 10px rgba(0,0,0,0.12)"; });
      box.addEventListener("pointerleave", () => { if (!dragId) box.style.boxShadow = ""; });
      // Drag to reorder (collapsed only — an expanded card is being edited). Blue
      // insertion line, exactly like the security search cards.
      if (!expanded) {
        box.draggable = true;
        box.addEventListener("dragstart", (e) => { dragId = strat.id; box.style.opacity = "0.5"; if (e.dataTransfer) e.dataTransfer.effectAllowed = "move"; });
        box.addEventListener("dragend", () => { dragId = null; box.style.opacity = ""; box.style.boxShadow = ""; });
        box.addEventListener("dragover", (e) => { if (!dragId || dragId === strat.id) return; e.preventDefault(); const r = box.getBoundingClientRect(); box.style.boxShadow = e.clientY > r.top + r.height / 2 ? "inset 0 -2px 0 var(--accent-blue)" : "inset 0 2px 0 var(--accent-blue)"; });
        box.addEventListener("dragleave", () => { box.style.boxShadow = ""; });
        box.addEventListener("drop", (e) => { e.preventDefault(); box.style.boxShadow = ""; if (dragId && dragId !== strat.id) { const r = box.getBoundingClientRect(); reorder(dragId, strat.id, e.clientY > r.top + r.height / 2); } });
      }
      // Name — double-click to rename in place; blank reverts to the old name.
      const nameEl = el("div", { text: strat.name || "Untitled strategy", attrs: { title: "Double-click to rename", class: "ta-strat-name" }, style: { fontWeight: "600", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text" } });
      nameEl.addEventListener("click", (e) => e.stopPropagation()); // clicking the name never toggles expand
      nameEl.addEventListener("dblclick", (e) => {
        e.stopPropagation();
        const input = el("input", { attrs: { type: "text", value: strat.name }, style: { width: "100%", boxSizing: "border-box", font: "inherit", fontWeight: "600", border: "1px solid var(--accent-blue,#2f6df6)", borderRadius: "6px", padding: "1px 5px" } }) as HTMLInputElement;
        let done = false;
        const commit = () => { if (done) return; done = true; const v = input.value.trim(); if (v) { strat.name = v; void saveBook(sym, book); } build(); };
        input.addEventListener("keydown", (ev) => { if (ev.key === "Enter") { ev.preventDefault(); commit(); } else if (ev.key === "Escape") { done = true; build(); } });
        input.addEventListener("blur", commit);
        input.addEventListener("click", (ev) => ev.stopPropagation());
        nameEl.replaceWith(input);
        input.focus(); input.select();
      });
      // Which tab this card is showing (only meaningful when expanded).
      const curTab: EditorTab = selTab.get(strat.id) ?? (strat.whys.length ? strat.whys[0]!.id : "manage");
      // Open a tab: select it and expand the card (a collapsed card expands on click).
      const openTab = (tab: EditorTab) => { selTab.set(strat.id, tab); book.activeId = strat.id; void saveBook(sym, book); build(); };

      // The icon strip is the whole trade-type palette (exactly like the
      // confluence icons on a security card): every type is always shown, its
      // label only a tooltip. A type that's switched on reads in colour inside a
      // circular highlight; the rest sit greyed with no background. Clicking a
      // greyed icon switches that type on and opens its settings; clicking an on
      // icon opens its settings; clicking the already-open one switches it off.
      const addWhy = (def: (typeof WHY_DEFS)[number]) => {
        const params: Record<string, number> = {};
        for (const d of def.dials ?? []) params[d.key] = d.def;
        // First type switched on is the trigger (the entry event); the rest filter it.
        strat.whys.push({ id: def.id, params, role: strat.whys.length === 0 ? "trigger" : "filter" });
        selTab.set(strat.id, def.id); book.activeId = strat.id; void saveBook(sym, book); build();
      };
      const removeWhy = (id: EditorTab) => {
        strat.whys = strat.whys.filter((w) => w.id !== id);
        selTab.set(strat.id, strat.whys.length ? strat.whys[0]!.id : "manage");
        void saveBook(sym, book); build();
      };

      // Hidden at rest, shown on card hover (CSS) in place of the name.
      const iconStrip = el("div", { attrs: { class: "ta-strat-icons" }, style: { alignItems: "center", gap: "3px", flexWrap: "wrap" } });
      for (const def of WHY_DEFS) {
        if (def.shape === "preview") continue; // "coming soon" types aren't selectable yet
        const w = strat.whys.find((x) => x.id === def.id);
        const active = !!w;
        const isFilter = w?.role === "filter";
        const selected = expanded && curTab === def.id;
        const btn = el("button", {
          attrs: { title: active ? `${def.label} — on (${isFilter ? "filter" : "trigger"})` : def.label },
          style: {
            display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px",
            borderRadius: "50%", border: selected ? "1px solid " + def.color : "1px solid transparent", cursor: "pointer",
            transition: "background .1s ease, opacity .1s ease",
            color: active ? def.color : "var(--text-secondary)", opacity: active ? "1" : "0.3",
            // On → circular tint; the open tab reads a touch stronger.
            background: active ? def.color + (selected ? "33" : "1f") : "none",
          },
        });
        btn.appendChild(svgIcon(WHY_ICON[def.id] ?? "", 14));
        btn.onclick = (e) => { e.stopPropagation(); if (!active) addWhy(def); else if (!selected) openTab(def.id); else removeWhy(def.id); };
        iconStrip.appendChild(btn);
      }
      // Manage cog — always available, sits after the palette.
      const mSel = expanded && curTab === "manage";
      const cog = el("button", { attrs: { title: "Manage the trade" }, style: { display: "inline-flex", alignItems: "center", justifyContent: "center", width: "24px", height: "24px", borderRadius: "50%", border: mSel ? "1px solid var(--text-secondary)" : "1px solid transparent", cursor: "pointer", marginLeft: "4px", color: "var(--text-secondary)", background: mSel ? "var(--text-secondary)22" : "none" } });
      cog.appendChild(svgIcon("M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5 M8 2v2 M8 12v2 M2 8h2 M12 8h2 M4 4l1.4 1.4 M10.6 10.6L12 12 M12 4l-1.4 1.4 M5.4 10.6L4 12", 14));
      cog.onclick = (e) => { e.stopPropagation(); openTab("manage"); };
      iconStrip.appendChild(cog);

      const head = el("div", { style: { display: "flex", alignItems: "flex-start", gap: "8px", padding: "9px 11px", cursor: "pointer" } }, [
        el("div", { style: { flex: "1", minWidth: "0" } }, [nameEl, iconStrip]),
      ]);
      head.onclick = () => { book.activeId = expanded ? null : strat.id; void saveBook(sym, book); build(); };
      // Canonical ✕ (top-right) to delete this strategy without expanding it.
      const del = closeButton(() => {
        book.strategies = book.strategies.filter((s) => s.id !== strat.id);
        if (book.activeId === strat.id) book.activeId = null;
        void saveBook(sym, book);
        build();
      }, { label: `Delete ${strat.name || "strategy"}`, size: 11 });
      del.addEventListener("click", (e) => e.stopPropagation()); // don't also toggle expand
      del.style.opacity = "0"; del.style.transition = "opacity .12s ease"; // reveal on hover, like the other cards
      box.addEventListener("pointerenter", () => { del.style.opacity = "1"; });
      box.addEventListener("pointerleave", () => { del.style.opacity = "0"; });
      head.appendChild(del);
      box.appendChild(head);
      if (expanded) {
        const bodyWrap = el("div", { style: { padding: "0 11px 10px" } });
        bodyWrap.appendChild(renderStrategyEditor(strat, {
          selected: curTab,
          onSelect: (tab) => { selTab.set(strat.id, tab); build(); },
          onChange: (next) => { const i = book.strategies.findIndex((s) => s.id === strat.id); if (i >= 0) { next.id = strat.id; book.strategies[i] = next; } persist(); },
          onRemove: () => { book.strategies = book.strategies.filter((s) => s.id !== strat.id); if (book.activeId === strat.id) book.activeId = null; void saveBook(sym, book); build(); },
          onDeploy: (s) => {
            const { strategy } = compileStudio(s, sym);
            saveNamedStrategy(strategy.name, strategy);
            saveStrategy(strategy, sym);
            dockIntoSidePanel();
            void storageSet("panelNavigate", { pageId: "deploy", at: Date.now() });
          },
        }));
        box.appendChild(bodyWrap);
      }
      list.appendChild(box);
    }
    // Centered grey "+" — same create affordance as the watchlists page.
    const plusBtn = pressable(el("button", { attrs: { title: "New strategy" }, style: { width: "34px", height: "34px", borderRadius: "999px", border: "0", background: "none", cursor: "pointer", color: "var(--text-secondary)", display: "flex", alignItems: "center", justifyContent: "center" } }));
    plusBtn.appendChild(svgIcon("M8 3v10M3 8h10", 18));
    plusBtn.onclick = addStrategy;
    list.appendChild(el("div", { style: { display: "flex", justifyContent: "center", padding: "6px 0" } }, [plusBtn]));

    root.replaceChildren(header, list);
    refreshSummaries();
  }

  async function refreshSummaries() {
    const stats = await loadStats(sym);
    for (const [id, node] of summaryNodes) node.textContent = summaryText(id, stats);
  }

  // Live: the chart writes stats here as you tune → update the collapsed summaries.
  const offStats = onStorageChange(statsStorageKey(sym), () => void refreshSummaries());
  void offStats; // onStorageChange returns void in this codebase; listener auto-lives for the page

  void loadBook(sym).then((b) => { book = b; build(); });

  return {
    el: root,
    dispose: () => { if (saveTimer != null) { clearTimeout(saveTimer); void saveBook(sym, book); } },
  };
}
